import React, { useState, useEffect } from "react";
import { Save, Plus, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { fileToResizedDataUrl } from "@/lib/image";
import { ICON_NAMES } from "@/lib/icons";
import { useSettings } from "@/context/SettingsContext";

const INPUT = "w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 text-sm";
const BORDER = { borderColor: "#dcfce7" };
const CARD = "bg-white rounded-xl border p-5 space-y-4";

export default function SettingsAdmin() {
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/site-settings").then((res) => setS(res.data));
  }, []);

  if (!s) return <p className="text-gray-400 py-10 text-center">Memuat...</p>;

  const set = (k, v) => setS({ ...s, [k]: v });

  const setItem = (key, idx, field, val) => {
    const arr = [...s[key]];
    arr[idx] = { ...arr[idx], [field]: val };
    setS({ ...s, [key]: arr });
  };
  const addItem = (key, obj) => setS({ ...s, [key]: [...(s[key] || []), obj] });
  const removeItem = (key, idx) => setS({ ...s, [key]: s[key].filter((_, i) => i !== idx) });

  const uploadTo = async (e, cb, maxDim = 1200) => {
    const file = e.target.files[0];
    if (!file) return;
    const dataUrl = await fileToResizedDataUrl(file, maxDim, 0.82);
    cb(dataUrl);
  };

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.put("/site-settings", s);
      await refresh();
      setMsg("Tersimpan!");
      setTimeout(() => setMsg(""), 2500);
    } catch (err) {
      setMsg("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Pengaturan Situs</h2>
        <div className="flex items-center gap-3">
          {msg && <span className="text-sm font-semibold" style={{ color: "#198530" }}>{msg}</span>}
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-save"
            className="inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-lg text-sm font-bold disabled:opacity-60"
            style={{ backgroundColor: "#198530" }}
          >
            <Save size={16} /> {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </button>
        </div>
      </div>

      {/* WhatsApp */}
      <div className={CARD} style={BORDER}>
        <h3 className="font-bold text-gray-800">Kontak WhatsApp</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nomor WA (format internasional)</label>
            <input
              className={INPUT}
              style={BORDER}
              value={s.whatsapp_number}
              onChange={(e) => set("whatsapp_number", e.target.value.replace(/[^0-9]/g, ""))}
              data-testid="settings-wa-number"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Tampilan Nomor</label>
            <input className={INPUT} style={BORDER} value={s.whatsapp_display} onChange={(e) => set("whatsapp_display", e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-gray-400">Link WhatsApp: https://wa.me/{s.whatsapp_number}</p>
      </div>

      {/* Hero Logo */}
      <div className={CARD} style={BORDER}>
        <h3 className="font-bold text-gray-800">Logo Hero (Beranda — kanan)</h3>
        <div className="flex items-center gap-4">
          {s.hero_logo ? (
            <img src={s.hero_logo} alt="logo" className="w-24 h-24 object-contain rounded-lg border" style={BORDER} />
          ) : (
            <div className="w-24 h-24 rounded-lg border flex items-center justify-center text-gray-300" style={BORDER}>
              <ImageIcon size={28} />
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer" style={{ backgroundColor: "#eef9f0", color: "#198530" }}>
            <Upload size={16} /> Ganti Logo
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadTo(e, (d) => set("hero_logo", d), 800)} data-testid="settings-logo" />
          </label>
        </div>
      </div>

      {/* ===== TEKS & TOMBOL HERO ===== */}
      <div className={CARD} style={BORDER}>
        <h3 className="font-bold text-gray-800">&#127968; Teks &amp; Tombol Hero (Beranda)</h3>
        <p className="text-xs text-gray-400">Sesuaikan teks dan tombol yang tampil di bagian Hero / Atas halaman beranda.</p>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Teks Badge (kecil di atas)</label>
            <input
              className={INPUT}
              style={BORDER}
              placeholder="AHLUSUNNAH WAL JAMAAH"
              value={s.hero_badge || ""}
              onChange={(e) => set("hero_badge", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Teks Arab (Bismillah / Ayat)</label>
            <input
              className={INPUT}
              style={BORDER}
              dir="rtl"
              placeholder="\u0628\u0650\u0633\u0652\u0645\u0650 \u0627\u0644\u0644\u0647\u0650..."
              value={s.hero_arabic || ""}
              onChange={(e) => set("hero_arabic", e.target.value)}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Judul (sebelum highlight)</label>
            <input
              className={INPUT}
              style={BORDER}
              placeholder="Yayasan Wakaf"
              value={s.hero_title || ""}
              onChange={(e) => set("hero_title", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Highlight (warna emas)</label>
            <input
              className={INPUT}
              style={BORDER}
              placeholder="Tongku Azhar"
              value={s.hero_title_highlight || ""}
              onChange={(e) => set("hero_title_highlight", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Judul (setelah highlight)</label>
            <input
              className={INPUT}
              style={BORDER}
              placeholder="Nahdliyyin"
              value={s.hero_title_suffix || ""}
              onChange={(e) => set("hero_title_suffix", e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Deskripsi / Subtitle</label>
          <textarea
            className={INPUT}
            style={BORDER}
            rows={3}
            placeholder="Mendidik dan mencetak generasi muda Islam..."
            value={s.hero_desc || ""}
            onChange={(e) => set("hero_desc", e.target.value)}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">Tombol Utama (Kuning)</label>
            <input
              className={INPUT}
              style={BORDER}
              placeholder="Teks tombol (mis. Daftar Sekarang)"
              value={s.hero_btn1_text || ""}
              onChange={(e) => set("hero_btn1_text", e.target.value)}
            />
            <input
              className={INPUT}
              style={BORDER}
              placeholder="Link tombol (mis. /informasi-pendaftaran)"
              value={s.hero_btn1_link || ""}
              onChange={(e) => set("hero_btn1_link", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">Tombol Kedua (link ke WhatsApp)</label>
            <input
              className={INPUT}
              style={BORDER}
              placeholder="Teks tombol (mis. Donasi Sekarang)"
              value={s.hero_btn2_text || ""}
              onChange={(e) => set("hero_btn2_text", e.target.value)}
            />
            <p className="text-xs text-gray-400">Link tombol kedua otomatis ke WhatsApp.</p>
          </div>
        </div>
      </div>

      {/* ===== KUTIPAN ISLAMI ===== */}
      <div className={CARD} style={BORDER}>
        <h3 className="font-bold text-gray-800">&#128172; Kutipan Islami (Beranda)</h3>
        <p className="text-xs text-gray-400">Teks kutipan di section hijau tua antara keunggulan dan artikel.</p>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Teks Arab / Hadits / Ayat</label>
          <input
            className={INPUT}
            style={BORDER}
            dir="rtl"
            placeholder="\u0637\u064e\u0644\u064e\u0628\u064f \u0627\u0644\u0652\u0639\u0650\u0644\u0652\u0645\u0650 \u0641\u064e\u0631\u0650\u064a\u0636\u064e\u0629\u064c"
            value={s.quote_arabic || ""}
            onChange={(e) => set("quote_arabic", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Terjemahan / Arti</label>
          <input
            className={INPUT}
            style={BORDER}
            placeholder='"Menuntut ilmu adalah kewajiban bagi setiap Muslim."'
            value={s.quote_translation || ""}
            onChange={(e) => set("quote_translation", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Sumber</label>
          <input
            className={INPUT}
            style={BORDER}
            placeholder="\u2014 Hadits Riwayat Ibnu Majah"
            value={s.quote_source || ""}
            onChange={(e) => set("quote_source", e.target.value)}
          />
        </div>
      </div>

      {/* Statistics */}
      <div className={CARD} style={BORDER}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Statistik</h3>
          <button
            type="button"
            onClick={() => addItem("stats", { number: "", label: "", icon: "Star" })}
            className="inline-flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: "#198530" }}
          >
            <Plus size={14} /> Statistik
          </button>
        </div>
        {(s.stats || []).map((st, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center">
            <input className={INPUT + " col-span-3"} style={BORDER} placeholder="Angka" value={st.number} onChange={(e) => setItem("stats", idx, "number", e.target.value)} />
            <input className={INPUT + " col-span-5"} style={BORDER} placeholder="Label" value={st.label} onChange={(e) => setItem("stats", idx, "label", e.target.value)} />
            <select className={INPUT + " col-span-3"} style={BORDER} value={st.icon} onChange={(e) => setItem("stats", idx, "icon", e.target.value)}>
              {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button type="button" onClick={() => removeItem("stats", idx)} className="col-span-1 p-2 rounded-lg bg-red-50 text-red-600 flex justify-center"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {/* Banners */}
      <div className={CARD} style={BORDER}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Banner Beranda (Hero Background)</h3>
          <label className="inline-flex items-center gap-1.5 text-sm font-semibold cursor-pointer" style={{ color: "#198530" }}>
            <Plus size={14} /> Tambah Banner
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadTo(e, (d) => addItem("banners", { image: d }), 1600)} data-testid="settings-banner-add" />
          </label>
        </div>
        <p className="text-xs text-gray-400">Banner tampil sebagai background slide Hero Beranda. Rasio ideal: landscape (3000x1407).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(s.banners || []).map((b, idx) => (
            <div key={idx} className="relative rounded-lg overflow-hidden border" style={BORDER}>
              <img src={b.image} alt={`banner-${idx}`} className="w-full h-28 object-contain bg-gray-50" />
              <button type="button" onClick={() => removeItem("banners", idx)} className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-600 text-white"><Trash2 size={14} /></button>
            </div>
          ))}
          {(s.banners || []).length === 0 && <p className="text-sm text-gray-400">Belum ada banner.</p>}
        </div>
      </div>

      {/* Profile */}
      <div className={CARD} style={BORDER}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Profil Yayasan (paragraf)</h3>
          <button
            type="button"
            onClick={() => set("profile_paragraphs", [...(s.profile_paragraphs || []), ""])}
            className="inline-flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: "#198530" }}
          >
            <Plus size={14} /> Paragraf
          </button>
        </div>
        {(s.profile_paragraphs || []).map((p, idx) => (
          <div key={idx} className="flex gap-2">
            <textarea
              className={INPUT}
              style={BORDER}
              rows={2}
              value={p}
              onChange={(e) => { const arr = [...s.profile_paragraphs]; arr[idx] = e.target.value; set("profile_paragraphs", arr); }}
            />
            <button
              type="button"
              onClick={() => set("profile_paragraphs", s.profile_paragraphs.filter((_, i) => i !== idx))}
              className="p-2 rounded-lg bg-red-50 text-red-600 flex-shrink-0"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* Programs */}
      <div className={CARD} style={BORDER}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Program Pendidikan</h3>
          <button
            type="button"
            onClick={() => addItem("programs", { level: "", name: "", desc: "" })}
            className="inline-flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: "#198530" }}
          >
            <Plus size={14} /> Program
          </button>
        </div>
        {(s.programs || []).map((p, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-start">
            <input className={INPUT + " col-span-2"} style={BORDER} placeholder="RA" value={p.level} onChange={(e) => setItem("programs", idx, "level", e.target.value)} />
            <input className={INPUT + " col-span-4"} style={BORDER} placeholder="Nama" value={p.name} onChange={(e) => setItem("programs", idx, "name", e.target.value)} />
            <input className={INPUT + " col-span-5"} style={BORDER} placeholder="Deskripsi" value={p.desc} onChange={(e) => setItem("programs", idx, "desc", e.target.value)} />
            <button type="button" onClick={() => removeItem("programs", idx)} className="col-span-1 p-2 rounded-lg bg-red-50 text-red-600 flex justify-center"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {/* Facilities */}
      <div className={CARD} style={BORDER}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Fasilitas Sekolah</h3>
          <button
            type="button"
            onClick={() => addItem("facilities", { name: "", desc: "", icon: "Building2", img: "" })}
            className="inline-flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: "#198530" }}
          >
            <Plus size={14} /> Fasilitas
          </button>
        </div>
        {(s.facilities || []).map((f, idx) => (
          <div key={idx} className="border rounded-lg p-3 space-y-2" style={BORDER}>
            <div className="grid grid-cols-12 gap-2 items-center">
              <input className={INPUT + " col-span-5"} style={BORDER} placeholder="Nama fasilitas" value={f.name} onChange={(e) => setItem("facilities", idx, "name", e.target.value)} />
              <select className={INPUT + " col-span-3"} style={BORDER} value={f.icon} onChange={(e) => setItem("facilities", idx, "icon", e.target.value)}>
                {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <label className="col-span-3 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-semibold cursor-pointer" style={{ backgroundColor: "#eef9f0", color: "#198530" }}>
                <Upload size={14} /> {f.img ? "Ganti" : "Foto"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadTo(e, (d) => setItem("facilities", idx, "img", d), 800)} />
              </label>
              <button type="button" onClick={() => removeItem("facilities", idx)} className="col-span-1 p-2 rounded-lg bg-red-50 text-red-600 flex justify-center"><Trash2 size={16} /></button>
            </div>
            <div className="flex gap-2 items-center">
              {f.img && <img src={f.img} alt="" className="w-16 h-12 object-cover rounded border" style={BORDER} />}
              <textarea
                className={INPUT}
                style={BORDER}
                rows={2}
                placeholder="Deskripsi"
                value={f.desc}
                onChange={(e) => setItem("facilities", idx, "desc", e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
